import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import useStyles from 'isomorphic-style-loader/useStyles';
import { ChevronDown, Wrench } from 'lucide-react';
import { Label, Popup } from 'semantic-ui-react';

import CopyButton from '@components/copy-button';
import { EPSSPopup } from '@components/epss-popup';
import { KEVIndicator } from '@components/kev-indicator';
import { PackageTypeIcon } from '@components/package-type';
import { getPrimaryCvss, SeverityLabel } from '@components/severity';
import TruncateList from '@components/truncate-list';

import type { VulnerabilityPackage } from '../../applications.models';
import { AffectedAssetsPopup } from '../affected-assets-popup';
import { VulnerabilityAnnotationControls } from '../vulnerability-annotations';

import s from './affected-packages-panel-section.scss';

export interface PackageSectionProps {
  appId: string;
  expanded: boolean;
  onToggle: () => void;
  pkg: VulnerabilityPackage;
  versionId: string;
  vulnerabilityId: string;
}

export const PackageSection = ({
  appId,
  expanded,
  onToggle,
  pkg,
  versionId,
  vulnerabilityId,
}: PackageSectionProps) => {
  useStyles(s);

  const cvssAssessment = getPrimaryCvss(pkg.cvssAssessments) ?? null;

  return (
    <div className={s.packageSection}>
      <button
        className={s.packageHeader}
        aria-expanded={expanded}
        aria-label={`${pkg.name} ${pkg.version}`}
        onClick={onToggle}
        type="button"
      >
        <PackageTypeIcon type={pkg.type} />
        <div className={s.packageInfo}>
          <span className={s.packageName}>
            {pkg.name} {pkg.version}
          </span>
          {pkg.purl && (
            <Popup
              content={
                // Stops the CopyButton click from bubbling to the parent
                // accordion <button> and toggling it closed.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div className={s.purlPopup} onClick={e => e.stopPropagation()}>
                  <span>{pkg.purl}</span>

                  <CopyButton
                    content={pkg.purl}
                    message="PURL copied to clipboard"
                    icon
                    inlineConfirmation
                  />
                </div>
              }
              mouseEnterDelay={400}
              position="left center"
              trigger={<span className={s.packagePurl}>{pkg.purl}</span>}
              flowing
              hoverable
            />
          )}
        </div>
        <ChevronDown
          className={classNames(s.chevron, { [s.chevronExpanded]: expanded })}
        />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className={s.packageDetailsWrapper}
            initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
            // Clear `overflow` after expansion so descendant menus and
            // popups can extend beyond the section.
            animate={{
              height: 'auto',
              opacity: 1,
              transitionEnd: { overflow: 'visible' },
            }}
            exit={{ height: 0, opacity: 0, overflow: 'hidden' }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className={s.packageDetails}>
              <div className={s.indicatorRow}>
                <span className={s.indicator}>
                  <span className={s.indicatorLabel}>CVSS</span>
                  <SeverityLabel
                    cvssAssessment={cvssAssessment}
                    size="mini"
                    variant="enhanced"
                    vulnerabilityId={vulnerabilityId}
                  />
                </span>

                <span className={s.indicator}>
                  <span className={s.indicatorLabel}>EPSS</span>
                  <EPSSPopup
                    score={pkg.epssScore ?? null}
                    percentile={pkg.epssPercentile ?? null}
                  />
                </span>

                <span className={s.indicator}>
                  <span className={s.indicatorLabel}>KEV</span>
                  <Label className={s.kevLabel}>
                    <KEVIndicator value={!!pkg.kev} />
                  </Label>
                </span>

                <span className={classNames(s.indicator, s.indicatorEnd)}>
                  <span className={s.indicatorLabel}>Assets</span>
                  <AffectedAssetsPopup
                    appId={appId}
                    count={pkg.assetCount ?? 0}
                    name={pkg.name}
                    namespace={pkg.namespace}
                    type={pkg.type}
                    version={pkg.version}
                    versionId={versionId}
                  />
                </span>
              </div>

              <VulnerabilityAnnotationControls
                appId={appId}
                pkg={pkg}
                versionId={versionId}
                vulnerabilityId={vulnerabilityId}
              />

              <div className={s.fixRow}>
                {pkg.fixVersions && pkg.fixVersions.length > 0 ? (
                  <>
                    <Wrench className={s.fixIcon} />
                    <span className={s.fixLabel}>Fixed in:</span>
                    <TruncateList
                      containerClassName={s.fixVersionsList}
                      list={pkg.fixVersions.map(fix => fix.version)}
                    />
                  </>
                ) : (
                  <>
                    <Wrench
                      className={classNames(s.fixIcon, s.fixIconUnavailable)}
                    />
                    <span className={s.noFixLabel}>No Fix Available</span>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
